import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Download, Star, CheckCircle2, Package, Users, DollarSign, Mail,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { API } from "@/lib/api";

const CATEGORIES = ["T-Shirts", "Stickers", "Hats", "Home Goods", "Custom Designs"];
const COLLECTIONS = ["Iconic KC", "Tornado Alley", "Custom Designs", "Stickers & Home Goods"];
const EMPTY = { name: "", category: "T-Shirts", price: "", description: "", image: "", collection: "Iconic KC", in_stock: true };

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border-2 border-[#1C1C1C] bg-[#F5F2EA] p-5 hard-shadow">
    <Icon className="w-7 h-7 text-[#D35400] mb-2" strokeWidth={2.2} />
    <div className="font-display text-4xl">{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#4A4A4A]">{label}</div>
  </div>
);

const Admin = () => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/subscriptions").then((r) => setSubs(r.data));
    api.get("/admin/contacts").then((r) => setContacts(r.data));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p, price: String(p.price) }); setDialogOpen(true); };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price required");
    const payload = { ...form, price: parseFloat(form.price) };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const feature = async (id) => {
    await api.post(`/products/${id}/feature`);
    toast.success("Set as featured monthly shirt");
    loadAll();
  };

  const fulfill = async (id) => {
    await api.post(`/admin/orders/${id}/fulfill`);
    toast.success("Marked fulfilled");
    loadAll();
  };

  const exportCsv = (path, filename) => {
    const token = localStorage.getItem("ta_token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        window.URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="admin-page">
      <h1 className="font-display text-6xl md:text-7xl mb-8">Admin Dashboard</h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <StatCard icon={Package} label="Products" value={stats.products} />
          <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(0)}`} />
          <StatCard icon={Package} label="Orders" value={stats.orders} />
          <StatCard icon={Users} label="Subscribers" value={stats.subscribers} />
          <StatCard icon={Mail} label="Newsletter" value={stats.newsletter} />
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="bg-[#EDE8DD] border-2 border-[#1C1C1C] rounded-none p-1 flex-wrap h-auto">
          <TabsTrigger value="products" className="rounded-none font-display text-lg data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-white" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-none font-display text-lg data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-white" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="subscribers" className="rounded-none font-display text-lg data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-white" data-testid="tab-subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="contacts" className="rounded-none font-display text-lg data-[state=active]:bg-[#1C1C1C] data-[state=active]:text-white" data-testid="tab-contacts">Requests</TabsTrigger>
        </TabsList>

        {/* PRODUCTS */}
        <TabsContent value="products" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-display text-3xl">Products</h2>
            <button onClick={openNew} className="btn-primary px-5 py-3 inline-flex items-center gap-2" data-testid="add-product-btn"><Plus className="w-5 h-5" /> Add Product</button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="border-2 border-[#1C1C1C] bg-[#F5F2EA] p-3 flex gap-3" data-testid={`admin-product-${p.id}`}>
                <img src={p.image} alt={p.name} className="w-20 h-20 object-cover border-2 border-[#1C1C1C]" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <h3 className="font-display text-xl leading-none truncate">{p.name}</h3>
                    {p.featured && <Star className="w-4 h-4 text-[#D35400] fill-[#D35400] shrink-0" />}
                  </div>
                  <p className="text-xs text-[#4A4A4A]">{p.category} · ${p.price.toFixed(2)}</p>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => openEdit(p)} className="text-[#2C3E50] hover:scale-110 transition" data-testid={`edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p.id)} className="text-[#C0392B] hover:scale-110 transition" data-testid={`delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    <button onClick={() => feature(p.id)} className="text-[#D35400] hover:scale-110 transition" title="Set as featured" data-testid={`feature-${p.id}`}><Star className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-display text-3xl">Orders</h2>
            <button onClick={() => exportCsv("/admin/export/orders", "orders.csv")} className="btn-ghost px-5 py-3 inline-flex items-center gap-2" data-testid="export-orders"><Download className="w-5 h-5" /> Export CSV</button>
          </div>
          {orders.length === 0 ? <p className="text-[#4A4A4A]">No orders yet.</p> : (
            <div className="space-y-3" data-testid="admin-orders-list">
              {orders.map((o) => (
                <div key={o.id} className="border-2 border-[#1C1C1C] bg-[#F5F2EA] p-4 flex flex-wrap justify-between gap-3 items-center" data-testid={`admin-order-${o.id}`}>
                  <div>
                    <p className="font-bold">{o.customer_name} <span className="text-xs text-[#4A4A4A]">· {o.email}</span></p>
                    <p className="text-sm text-[#4A4A4A]">{o.items.map((i) => `${i.name} (${i.size})×${i.quantity}`).join(", ")}</p>
                    <p className="text-xs text-[#4A4A4A]">{o.shipping?.address}, {o.shipping?.city}, {o.shipping?.state} {o.shipping?.zip_code}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-[#C0392B]">${o.total.toFixed(2)}</span>
                    {o.fulfilled ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase text-[#27ae60]"><CheckCircle2 className="w-4 h-4" /> Fulfilled</span>
                    ) : (
                      <button onClick={() => fulfill(o.id)} className="text-xs font-bold uppercase bg-[#1C1C1C] text-white px-3 py-2 hover:bg-[#D35400] transition" data-testid={`fulfill-${o.id}`}>Mark Fulfilled</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SUBSCRIBERS */}
        <TabsContent value="subscribers" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-display text-3xl">Subscribers</h2>
            <button onClick={() => exportCsv("/admin/export/subscribers", "subscribers.csv")} className="btn-ghost px-5 py-3 inline-flex items-center gap-2" data-testid="export-subscribers"><Download className="w-5 h-5" /> Export CSV</button>
          </div>
          {subs.length === 0 ? <p className="text-[#4A4A4A]">No subscribers yet.</p> : (
            <div className="space-y-3" data-testid="admin-subs-list">
              {subs.map((s) => (
                <div key={s.id} className="border-2 border-[#1C1C1C] bg-[#F5F2EA] p-4 flex flex-wrap justify-between gap-2" data-testid={`admin-sub-${s.id}`}>
                  <div>
                    <p className="font-bold">{s.customer_name} <span className="text-xs text-[#4A4A4A]">· {s.email}</span></p>
                    <p className="text-sm text-[#4A4A4A]">{s.fit} · Size {s.size} · ${s.price.toFixed(2)}/mo</p>
                  </div>
                  <span className={`text-xs font-bold uppercase h-fit px-2 py-1 ${s.status === "active" ? "bg-[#2C3E50] text-white" : "bg-[#C0392B] text-white"}`}>{s.status}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts" className="mt-6">
          <h2 className="font-display text-3xl mb-4">Design Requests & Messages</h2>
          {contacts.length === 0 ? <p className="text-[#4A4A4A]">No messages yet.</p> : (
            <div className="space-y-3" data-testid="admin-contacts-list">
              {contacts.map((c) => (
                <div key={c.id} className="border-2 border-[#1C1C1C] bg-[#F5F2EA] p-4" data-testid={`admin-contact-${c.id}`}>
                  <div className="flex justify-between gap-2">
                    <p className="font-bold">{c.name} <span className="text-xs text-[#4A4A4A]">· {c.email}</span></p>
                    <span className="text-xs font-bold uppercase px-2 py-1 bg-[#D35400] text-white h-fit">{c.type === "custom_design" ? "Custom" : "General"}</span>
                  </div>
                  <p className="text-sm text-[#4A4A4A] mt-2">{c.message}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* PRODUCT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#FDFBF7] border-2 border-[#1C1C1C] rounded-none max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <input placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="form-name" />
            <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400] resize-none" data-testid="form-description" />
            <div className="grid grid-cols-2 gap-3">
              <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="form-price" />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="border-2 border-[#1C1C1C] rounded-none bg-white" data-testid="form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Select value={form.collection} onValueChange={(v) => setForm({ ...form, collection: v })}>
              <SelectTrigger className="border-2 border-[#1C1C1C] rounded-none bg-white" data-testid="form-collection"><SelectValue /></SelectTrigger>
              <SelectContent>{COLLECTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <input placeholder="Image URL" value={form.image?.startsWith("data:") ? "" : form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="w-full px-4 py-3 border-2 border-[#1C1C1C] bg-white focus:outline-none focus:border-[#D35400]" data-testid="form-image-url" />
            <div>
              <label className="text-sm font-semibold">Or upload image:</label>
              <input type="file" accept="image/*" onChange={onFile} className="block mt-1 text-sm" data-testid="form-image-file" />
            </div>
            {form.image && <img src={form.image} alt="preview" className="w-24 h-24 object-cover border-2 border-[#1C1C1C]" />}
          </div>
          <DialogFooter>
            <button onClick={save} className="btn-primary px-8 py-3 w-full" data-testid="save-product">{editing ? "Update" : "Add"} Product</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
